/**
 * Local player progress store (localStorage). The local copy is the source of
 * truth; D1 mirroring/sync arrives in M5 (spec §12).
 */

import {
  type MetaEffects,
  SKILLS,
  computeEffects,
  getSkill,
  skillCost,
} from "../game/skills";
import {
  type AchievementContext,
  type AchievementDef,
  getAchievement,
  newlyUnlocked,
} from "../game/achievements";
import { dayDiff } from "../game/daily";
import { STAGES } from "../game/stages";
import {
  type MissionDef,
  type MissionRunFacts,
  getMission,
  loginReward,
  missionContribution,
  missionsForDate,
  scaledTarget,
} from "../game/missions";

export interface StageClear {
  stars: number; // best 0..3
  bestScore: number;
  bestDrops: number; // fewest drops to clear
}

/** Board widths that gate the column picker. */
export const BASE_COLS = 4;

export interface ProgressData {
  accountLevel: number;
  accountXp: number;
  stardust: number;
  maxTierReached: number;
  /** stage id → board-width → best clear record. */
  clears: Record<string, Record<number, StageClear>>;
  /** stage id → board widths it has been cleared at (gates the column picker). */
  clearCols: Record<string, number[]>;
  /** Unlocked stage ids / feature keys. */
  unlocks: string[];
  bestEndless: number;
  /** Best endless score per board width (4/5/6). */
  bestEndlessByCols: Record<number, number>;
  /** skill id → level (skills are bought with stardust). */
  skills: Record<string, number>;
  /** Unlocked achievement ids. */
  achievements: string[];
  /** Last daily-challenge date played for reward (YYYY-MM-DD). */
  dailyLastDate: string;
  /** Consecutive-day daily streak. */
  dailyStreak: number;
  /** Best daily score, keyed by date. */
  dailyBest: Record<string, number>;
  /** Date (YYYY-MM-DD) the current daily missions belong to. */
  missionDay: string;
  /** mission id → accumulated progress for the current day. */
  missionProgress: Record<string, number>;
  /** Board width snapshot when missions were rolled (scales targets per width). */
  missionCols: number;
  /** Mission ids already claimed today. */
  missionClaimed: string[];
  /** Last date the login reward was claimed. */
  loginLastDate: string;
  /** Consecutive-day login streak. */
  loginStreak: number;
  /** Lifetime count of missions claimed (across all days). */
  missionsClaimedTotal: number;
  /** Lifetime discovery flags for the collection (specials encyclopedia). */
  seenWild: boolean;
  seenBomb: boolean;
  seenBoss: boolean;
  /** Purchased cosmetic ids ("skin:neon", "theme:nebula", ...). */
  ownedCosmetics: string[];
  /** First-time tutorial completed or skipped. */
  tutorialDone: boolean;
  /** Last local mutation time (ms) — used for server sync conflict resolution. */
  updatedAt: number;
}

const KEY = "cm.progress.v1";

function defaults(): ProgressData {
  return {
    accountLevel: 1,
    accountXp: 0,
    stardust: 0,
    maxTierReached: 1,
    clears: {},
    clearCols: {},
    unlocks: [],
    bestEndless: 0,
    bestEndlessByCols: {},
    skills: {},
    achievements: [],
    dailyLastDate: "",
    dailyStreak: 0,
    dailyBest: {},
    missionDay: "",
    missionProgress: {},
    missionCols: BASE_COLS,
    missionClaimed: [],
    loginLastDate: "",
    loginStreak: 0,
    missionsClaimedTotal: 0,
    seenWild: false,
    seenBomb: false,
    seenBoss: false,
    ownedCosmetics: [],
    tutorialDone: false,
    updatedAt: 0,
  };
}

function nowMs(): number {
  return Date.now();
}

/**
 * Migrate older saves where `clears[stageId]` was a flat StageClear (no width
 * dimension). Fold those records into the 4-cols slot — that's the width all
 * pre-migration players used — and leave the new shape alone if it's already
 * present.
 */
function migrateClears(data: ProgressData): ProgressData {
  const out: Record<string, Record<number, StageClear>> = {};
  for (const [id, rec] of Object.entries(data.clears ?? {})) {
    if (rec && typeof rec === "object" && "stars" in (rec as object)) {
      // Legacy flat StageClear: bucket it under 4 cols.
      out[id] = { [BASE_COLS]: rec as unknown as StageClear };
    } else {
      // Already a per-cols map (numeric keys arrive as strings from JSON).
      const perCols: Record<number, StageClear> = {};
      for (const [k, v] of Object.entries(rec as Record<string, StageClear>)) {
        perCols[Number(k)] = v;
      }
      out[id] = perCols;
    }
  }
  data.clears = out;
  return data;
}

/** XP needed to advance FROM `level` to the next (spec §7.1: base * level^1.5). */
export function xpForLevel(level: number): number {
  return Math.round(100 * Math.pow(level, 1.5));
}

export class Progress {
  private data: ProgressData;
  private listeners = new Set<(data: Readonly<ProgressData>) => void>();

  constructor() {
    this.data = this.load();
  }

  private load(): ProgressData {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return migrateClears({ ...defaults(), ...JSON.parse(raw) });
    } catch {
      /* ignore */
    }
    return defaults();
  }

  /** Persist to localStorage and notify listeners (no timestamp bump). */
  private persist(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      /* ignore */
    }
    for (const fn of this.listeners) fn(this.data);
  }

  /** Save a local mutation: bumps `updatedAt` then persists. */
  save(): void {
    this.data.updatedAt = nowMs();
    this.persist();
  }

  /** Subscribe to progress changes (for the sync client). Returns unsubscribe. */
  onChange(fn: (data: Readonly<ProgressData>) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Replace the entire data set (e.g. after a server merge). Keeps `updatedAt`. */
  replace(data: ProgressData): void {
    this.data = { ...defaults(), ...data };
    this.persist();
  }

  /** Wipe all progress back to a fresh account (spec §8.6, 2-step confirm in UI). */
  reset(): void {
    this.data = defaults();
    this.save();
  }

  // ---- Achievements (spec §6.2) ----

  hasAchievement(id: string): boolean {
    return this.data.achievements.includes(id);
  }

  /** Build the evaluation context from current progress + optional run facts. */
  achievementContext(
    lastChain?: number,
    colsPlayed?: number,
    abilitiesUsedThisRun?: string[],
    usedWildThisRun?: boolean,
    usedBombThisRun?: boolean,
  ): AchievementContext {
    // For achievement evaluation use the highest stars earned for each stage
    // across ANY width — so "perfect a chapter" milestones can be hit by
    // accumulating 3 stars at any one width per stage.
    const bestStars: Record<string, number> = {};
    for (const [id, perCols] of Object.entries(this.data.clears)) {
      let best = 0;
      for (const c of Object.values(perCols)) if (c.stars > best) best = c.stars;
      bestStars[id] = best;
    }
    return {
      maxTierReached: this.data.maxTierReached,
      bestEndless: this.data.bestEndless,
      totalStars: this.totalStarsAllWidths(),
      clearStars: bestStars,
      lastChain,
      anyClear: Object.keys(this.data.clears).length > 0,
      anyMaxedSkill: this.anyMaxedSkill(),
      colsPlayed,
      loginStreak: this.data.loginStreak ?? 0,
      missionsClaimedTotal: this.data.missionsClaimedTotal ?? 0,
      clearedWidths: this.data.clearCols ?? {},
      abilitiesUsedThisRun,
      usedWildThisRun,
      usedBombThisRun,
    };
  }

  /** Unlock any newly-satisfied achievements, granting their rewards. */
  checkAchievements(
    lastChain?: number,
    colsPlayed?: number,
    abilitiesUsedThisRun?: string[],
    usedWildThisRun?: boolean,
    usedBombThisRun?: boolean,
  ): AchievementDef[] {
    const owned = new Set(this.data.achievements);
    const ids = newlyUnlocked(
      this.achievementContext(
        lastChain,
        colsPlayed,
        abilitiesUsedThisRun,
        usedWildThisRun,
        usedBombThisRun,
      ),
      owned,
    );
    const unlocked: AchievementDef[] = [];
    for (const id of ids) {
      const def = getAchievement(id);
      if (!def) continue;
      this.data.achievements.push(id);
      this.data.stardust += Math.round(def.reward * this.effects().stardustMult);
      unlocked.push(def);
    }
    if (unlocked.length) this.save();
    return unlocked;
  }

  // ---- Daily challenge (spec §5.5) ----

  /** Whether the daily reward for `dateKey` has already been claimed. */
  dailyClaimed(dateKey: string): boolean {
    return this.data.dailyLastDate === dateKey;
  }

  /**
   * Record a finished daily run. The first finish each day grants a streak
   * reward (base + streak bonus); later finishes only update the best score.
   */
  recordDaily(dateKey: string, score: number): { reward: number; streak: number; firstToday: boolean } {
    this.data.dailyBest[dateKey] = Math.max(this.data.dailyBest[dateKey] ?? 0, score);
    if (this.dailyClaimed(dateKey)) {
      this.save();
      return { reward: 0, streak: this.data.dailyStreak, firstToday: false };
    }
    const gap = this.data.dailyLastDate ? dayDiff(this.data.dailyLastDate, dateKey) : Infinity;
    this.data.dailyStreak = gap === 1 ? this.data.dailyStreak + 1 : 1;
    this.data.dailyLastDate = dateKey;
    // Base + streak bonus, capped so a long streak can't snowball indefinitely.
    const base = 60;
    const raw = Math.min(base + (this.data.dailyStreak - 1) * 15, 150);
    const reward = Math.round(raw * this.effects().stardustMult);
    this.data.stardust += reward;
    this.save();
    return { reward, streak: this.data.dailyStreak, firstToday: true };
  }

  // ---- Daily missions & login reward (recurring stardust) ----

  /** Reset the day's mission progress when the date rolls over. */
  private rollMissions(dateKey: string, cols: number): void {
    if (this.data.missionDay === dateKey) return;
    this.data.missionDay = dateKey;
    this.data.missionCols = cols;
    this.data.missionProgress = {};
    this.data.missionClaimed = [];
  }

  /** Mission target after scaling for the day's snapshot board width. */
  private targetFor(def: MissionDef): number {
    return scaledTarget(def, this.data.missionCols || BASE_COLS);
  }

  /**
   * Today's missions with live progress + claim state (resets on a new day).
   * Targets are scaled by the board width active when missions were rolled.
   */
  dailyMissions(
    dateKey: string,
    cols: number = this.data.missionCols || BASE_COLS,
  ): Array<{ def: MissionDef; current: number; target: number; done: boolean; claimed: boolean }> {
    if (this.data.missionDay !== dateKey) {
      this.rollMissions(dateKey, cols);
      this.save();
    }
    return missionsForDate(dateKey).map((def) => {
      const target = this.targetFor(def);
      const current = Math.min(this.data.missionProgress[def.id] ?? 0, target);
      const claimed = this.data.missionClaimed.includes(def.id);
      return { def, current, target, done: current >= target, claimed };
    });
  }

  /** Apply a finished run toward today's missions. */
  recordMissions(
    dateKey: string,
    facts: MissionRunFacts,
    cols: number = this.data.missionCols || BASE_COLS,
  ): void {
    if (this.data.missionDay !== dateKey) this.rollMissions(dateKey, cols);
    for (const def of missionsForDate(dateKey)) {
      const contrib = missionContribution(def.metric, facts);
      if (contrib <= 0) continue;
      const cur = this.data.missionProgress[def.id] ?? 0;
      this.data.missionProgress[def.id] =
        def.agg === "max" ? Math.max(cur, contrib) : cur + contrib;
    }
    this.save();
  }

  /** Claim a completed, unclaimed mission. Returns the stardust granted (0 if not claimable). */
  claimMission(dateKey: string, id: string): number {
    if (this.data.missionDay !== dateKey) return 0;
    const def = getMission(id);
    if (!def || this.data.missionClaimed.includes(id)) return 0;
    if ((this.data.missionProgress[id] ?? 0) < this.targetFor(def)) return 0;
    this.data.missionClaimed.push(id);
    this.data.missionsClaimedTotal = (this.data.missionsClaimedTotal ?? 0) + 1;
    const reward = Math.round(def.reward * this.effects().stardustMult);
    this.data.stardust += reward;
    this.save();
    return reward;
  }

  /** Login-reward state for the day (amount reflects the streak if unclaimed). */
  loginRewardState(dateKey: string): { claimed: boolean; amount: number; streak: number } {
    const claimed = this.data.loginLastDate === dateKey;
    const streak = claimed
      ? this.data.loginStreak
      : this.data.loginLastDate && dayDiff(this.data.loginLastDate, dateKey) === 1
        ? this.data.loginStreak + 1
        : 1;
    return { claimed, amount: loginReward(streak), streak };
  }

  /** Claim today's login reward once. Returns granted amount + streak, or null. */
  claimLogin(dateKey: string): { amount: number; streak: number } | null {
    if (this.data.loginLastDate === dateKey) return null;
    const cont = Boolean(this.data.loginLastDate) && dayDiff(this.data.loginLastDate, dateKey) === 1;
    this.data.loginStreak = cont ? this.data.loginStreak + 1 : 1;
    this.data.loginLastDate = dateKey;
    const amount = Math.round(loginReward(this.data.loginStreak) * this.effects().stardustMult);
    this.data.stardust += amount;
    this.save();
    return { amount, streak: this.data.loginStreak };
  }

  get snapshot(): Readonly<ProgressData> {
    return this.data;
  }

  /** Per-cols clear record (or undefined if never cleared at that width). */
  clearFor(stageId: string, cols: number): StageClear | undefined {
    return this.data.clears[stageId]?.[cols];
  }

  /** Stars at the given board width (independent across widths). */
  starsFor(stageId: string, cols: number): number {
    return this.data.clears[stageId]?.[cols]?.stars ?? 0;
  }

  /** Total stars across all stages at the given width. */
  totalStars(cols: number): number {
    let sum = 0;
    for (const perCols of Object.values(this.data.clears)) {
      sum += perCols[cols]?.stars ?? 0;
    }
    return sum;
  }

  /** Total stars summed across every width (used for global milestones). */
  totalStarsAllWidths(): number {
    let sum = 0;
    for (const perCols of Object.values(this.data.clears)) {
      for (const c of Object.values(perCols)) sum += c.stars;
    }
    return sum;
  }

  /**
   * Whether a stage is selectable at the given width — clearing its prerequisite
   * at the SAME width is what unlocks it (per-cols progression).
   */
  isUnlocked(_stageId: string, requiresStage: string | undefined, cols: number): boolean {
    // First stage of a chapter (no prerequisite) is always available.
    if (!requiresStage) return true;
    // Per-cols progression: the prerequisite must be cleared at THIS width.
    // We deliberately do NOT honor `data.unlocks.includes(_stageId)` here —
    // that list is for non-stage feature flags (e.g. "chapter:1-complete")
    // and would otherwise let a 4-col clear unlock the same stage at 5/6 cols.
    return Boolean(this.data.clears[requiresStage]?.[cols]);
  }

  // ---- Board-width progression (column picker, home only) ----

  /** True when every stage of `world` has been cleared at the given width. */
  worldClearedAt(world: number, cols: number): boolean {
    const stages = STAGES.filter((s) => s.world === world);
    if (stages.length === 0) return false;
    return stages.every((s) => (this.data.clearCols[s.id] ?? []).includes(cols));
  }

  /**
   * Widest board the player may select. Starts at 4; clearing World 1 at the
   * current width unlocks the next (5, then 6).
   */
  maxColsUnlocked(): number {
    if (this.worldClearedAt(1, 5)) return 6;
    if (this.worldClearedAt(1, 4)) return 5;
    return BASE_COLS;
  }

  /** Whether a given board width is currently selectable. */
  colsUnlocked(cols: number): boolean {
    return cols <= this.maxColsUnlocked();
  }

  /** Clamp a requested width to what's unlocked (and the 4..6 range). */
  effectiveCols(requested: number): number {
    const want = Math.min(6, Math.max(BASE_COLS, Math.floor(requested || BASE_COLS)));
    return Math.min(want, this.maxColsUnlocked());
  }

  addStardust(n: number): void {
    this.data.stardust += n;
  }

  /** Add XP and apply level-ups. Returns levels gained. */
  addXp(n: number): number {
    this.data.accountXp += n;
    let gained = 0;
    while (this.data.accountXp >= xpForLevel(this.data.accountLevel)) {
      this.data.accountXp -= xpForLevel(this.data.accountLevel);
      this.data.accountLevel += 1;
      gained += 1;
    }
    return gained;
  }

  // ---- Skills (spec §7.2 — bought with stardust) ----

  skillLevel(id: string): number {
    return this.data.skills[id] ?? 0;
  }

  /** Stardust cost to raise a skill to its next level, or null if maxed. */
  skillNextCost(id: string): number | null {
    const def = getSkill(id);
    if (!def) return null;
    const cur = this.skillLevel(id);
    if (cur >= def.max) return null;
    return skillCost(def, cur);
  }

  /** Spend stardust to raise a skill, if affordable and below max. */
  upgradeSkill(id: string): boolean {
    const cost = this.skillNextCost(id);
    if (cost == null || this.data.stardust < cost) return false;
    this.data.stardust -= cost;
    this.data.skills[id] = this.skillLevel(id) + 1;
    this.save();
    return true;
  }

  /** True when any skill has reached its max level. */
  anyMaxedSkill(): boolean {
    return SKILLS.some((s) => this.skillLevel(s.id) >= s.max);
  }

  /** Aggregate gameplay/meta modifiers from skills. */
  effects(): MetaEffects {
    return computeEffects(this.data.skills);
  }

  noteMaxTier(tier: number): void {
    if (tier > this.data.maxTierReached) this.data.maxTierReached = tier;
  }

  markTutorialDone(): void {
    if (this.data.tutorialDone) return;
    this.data.tutorialDone = true;
    this.save();
  }

  // ---- Cosmetics (ideas #10/#11, spec §15.3) ----

  ownsCosmetic(id: string): boolean {
    return (this.data.ownedCosmetics ?? []).includes(id);
  }

  /** Buy a cosmetic with stardust. Returns true on success. */
  buyCosmetic(id: string, cost: number): boolean {
    if (this.ownsCosmetic(id) || this.data.stardust < cost) return false;
    this.data.stardust -= cost;
    this.data.ownedCosmetics = [...(this.data.ownedCosmetics ?? []), id];
    this.save();
    return true;
  }

  /** Record lifetime special-particle discoveries (collection entries). */
  noteSpecialsSeen(wild: boolean, bomb: boolean, boss: boolean): void {
    let changed = false;
    if (wild && !this.data.seenWild) {
      this.data.seenWild = true;
      changed = true;
    }
    if (bomb && !this.data.seenBomb) {
      this.data.seenBomb = true;
      changed = true;
    }
    if (boss && !this.data.seenBoss) {
      this.data.seenBoss = true;
      changed = true;
    }
    if (changed) this.save();
  }

  recordEndless(score: number, cols?: number): void {
    let changed = false;
    if (score > this.data.bestEndless) {
      this.data.bestEndless = score;
      changed = true;
    }
    if (cols != null) {
      const w = Math.min(6, Math.max(4, Math.floor(cols)));
      if (!this.data.bestEndlessByCols) this.data.bestEndlessByCols = {};
      if (score > (this.data.bestEndlessByCols[w] ?? 0)) {
        this.data.bestEndlessByCols[w] = score;
        changed = true;
      }
    }
    if (changed) this.save();
  }

  /** Best endless score for a board width; cols 0 = best across all widths. */
  bestEndlessFor(cols: number): number {
    const byCols = this.data.bestEndlessByCols ?? {};
    if (cols === 4 || cols === 5 || cols === 6) return byCols[cols] ?? 0;
    const vals = Object.values(byCols);
    return vals.length ? Math.max(this.data.bestEndless, ...vals) : this.data.bestEndless;
  }

  unlock(key: string): void {
    if (key && !this.data.unlocks.includes(key)) this.data.unlocks.push(key);
  }

  /**
   * Record a finished stage run. Applies first-clear / per-new-star / repeat
   * rewards (spec §6.3) and updates best records. Returns the reward summary.
   */
  recordStageResult(
    stageId: string,
    cleared: boolean,
    stars: number,
    score: number,
    drops: number,
    cols: number,
    rewards: import("../game/types").StageRewards,
    unlockId?: string,
  ): { stardust: number; xp: number; levelsGained: number; newStars: number; firstClear: boolean } {
    let stardust = 0;
    let xp = 0;
    let firstClear = false;
    let newStars = 0;

    if (cleared) {
      const perCols = this.data.clears[stageId] ?? {};
      const prev = perCols[cols];
      firstClear = !prev;
      const prevStars = prev?.stars ?? 0;
      newStars = Math.max(0, stars - prevStars);

      if (firstClear) {
        stardust += rewards.firstClear?.stardust ?? 0;
        xp += rewards.firstClear?.xp ?? 0;
        // First clear at THIS width unlocks the next stage at this width.
        if (unlockId) this.unlock(unlockId);
      } else {
        stardust += rewards.repeat?.stardust ?? 0;
        xp += rewards.repeat?.xp ?? 0;
      }
      // Reward only newly-earned stars.
      stardust += newStars * (rewards.perStar?.stardust ?? 0);
      xp += newStars * (rewards.perStar?.xp ?? 0);

      // Apply meta multipliers (economy skills + prestige).
      const fx = this.effects();
      stardust = Math.round(stardust * fx.stardustMult);
      xp = Math.round(xp * fx.xpMult);

      perCols[cols] = {
        stars: Math.max(prevStars, stars),
        bestScore: Math.max(prev?.bestScore ?? 0, score),
        bestDrops: prev ? Math.min(prev.bestDrops, drops) : drops,
      };
      this.data.clears[stageId] = perCols;

      // Track the board width this clear happened at (gates the column picker).
      const widths = this.data.clearCols[stageId] ?? [];
      if (!widths.includes(cols)) {
        this.data.clearCols[stageId] = [...widths, cols].sort((a, b) => a - b);
      }
    }

    this.addStardust(stardust);
    const levelsGained = this.addXp(xp);
    this.save();
    return { stardust, xp, levelsGained, newStars, firstClear };
  }
}

export const progress = new Progress();
